'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { ChevronLeft, Radio } from 'lucide-react'
import Turnstile, { type TurnstileHandle } from '../../../Turnstile'
import { fmtDatePt } from '../cttUtils'
import type { ProcessedTheme, BrowserRow } from '../../../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate, postTypeLabel } from '../../../ProcessedEntry/DomainEntry/processedTypes'
import { OriginalSubmissionWidget } from '../../../ProcessedEntry/OriginalSubmissionWidget'
import { LatestObtainedImagesPanel } from '../../../ProcessedEntry/DomainEntry/LatestObtainedImagesPanel'
import { CampaignForm } from '../shared/CampaignForm'
import type { RawEntry } from '../../trustCenterTypes'
import { parseDbTimestamp } from '@/lib/dates'

const TURNSTILE_SITEKEY = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_TF_CENTER_GETENTRIES_KEY ?? ''

export function CttProcessedEntryDetail({
  entry,
  theme,
  onBack,
  onPromoted,
  recentCampaigns = [],
}: {
  entry: RawEntry
  theme: ProcessedTheme
  onBack: () => void
  onPromoted: () => void
  recentCampaigns?: { headline: string; description: string; image_count: number }[]
}) {
  const isDark = theme === 'dark'
  const c = watchClasses(theme)
  const border = 'border-border'
  const muted  = 'text-muted-foreground'

  const [browserRows, setBrowserRows] = useState<BrowserRow[]>([])
  const [browserRowsLoading, setBrowserRowsLoading] = useState(false)
  const [autoScreenshots, setAutoScreenshots] = useState<{
    desktop: string | null
    mobile: string | null
  } | null>(null)
  const [submissionImage, setSubmissionImage] = useState<{ base64: string; mime: string } | null>(null)

  const turnstileRef = useRef<TurnstileHandle>(null)
  const pendingTokensRef = useRef<((token: string) => void)[]>([])

  const handleVerify = useCallback((token: string) => {
    const resolve = pendingTokensRef.current.shift()
    if (resolve) resolve(token)
    if (pendingTokensRef.current.length > 0) {
      turnstileRef.current?.reset()
      turnstileRef.current?.run()
    }
  }, [])

  const getToken = useCallback((): Promise<string> => {
    return new Promise((resolve) => {
      pendingTokensRef.current.push(resolve)
      if (pendingTokensRef.current.length === 1) {
        turnstileRef.current?.reset()
        turnstileRef.current?.run()
      }
    })
  }, [])

  useEffect(() => {
    if (!entry.user_entry_domain_id) return
    setBrowserRowsLoading(true)
    fetch(`/api/campaigns/ctt/entry-domains?userEntryDomainId=${entry.user_entry_domain_id}`)
      .then((r) => r.json())
      .then((res) => {
        if (res.success) {
          const filtered = (res.data?.browserData ?? []).filter(
            (r: BrowserRow) =>
              r.entryDomainId === entry.user_entry_domain_id &&
              r.r2Key &&
              (r.mobileIsAlive !== null || r.desktopIsAlive !== null),
          )
          setBrowserRows(filtered)
        }
      })
      .catch(() => null)
      .finally(() => setBrowserRowsLoading(false))
  }, [entry.user_entry_domain_id])

  const entryTitle = entry.headline ?? entry.message_content?.slice(0, 80) ?? '—'
  const dayMonth = (() => {
    const d = parseDbTimestamp(entry.created_at)
    if (!d) return '—'
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
  })()

  return (
    <div className={`min-h-screen ${c.shell}`}>
      <Turnstile
        ref={turnstileRef}
        sitekey={TURNSTILE_SITEKEY}
        onVerify={handleVerify}
        onReady={() => {
          if (pendingTokensRef.current.length > 0) turnstileRef.current?.run()
        }}
      />

      <div className="mx-auto max-w-[1280px] px-6 py-6">
        {/* Back button */}
        <button
          onClick={onBack}
          className={`mb-5 inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${c.line} ${c.card} ${c.text}`}
        >
          <ChevronLeft className="h-4 w-4" />
          Back to trust center entries
        </button>

        {/* Entry card — bento grid */}
        <article className={`border overflow-hidden mb-6 ${c.line} ${c.card}`}>
          <div className="grid grid-cols-[auto_1fr_1fr_auto_280px]">

            {/* Cell 1: meta chips + date stat */}
            <div className={`flex flex-col justify-between gap-6 px-6 py-6 border-r ${border}`}>
              <div className="flex flex-col gap-2">
                <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 w-fit ${c.line} ${c.soft}`}>
                  <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Entry</span>
                  <span className={`font-mono text-[10px] ${c.muted}`}>{entry.id.slice(0, 8)}…</span>
                </span>
                <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 w-fit bg-warning-surface border-warning-border text-warning`}>
                  Processed
                </span>
                <span className={`text-xs ${c.muted}`}>first seen {compactDate(entry.created_at)}</span>
              </div>
              <div>
                <div className={`font-mono text-[34px] leading-none tracking-tight ${c.text}`}>{dayMonth}</div>
                <div className={`mt-1 font-mono text-[10px] uppercase tracking-[0.3em] ${c.label}`}>submitted</div>
              </div>
            </div>

            {/* Cell 2: headline */}
            <div className={`flex items-start px-6 py-6 border-r ${border}`}>
              <h2 className={`text-xl font-semibold leading-snug tracking-[-0.01em] ${c.text}`}>
                "{entryTitle}"
              </h2>
            </div>

            {/* Cell 3: message + pills */}
            <div className={`flex flex-col justify-between gap-4 px-6 py-6 border-r ${border}`}>
              {entry.message_content && (
                <p className={`text-[15px] leading-7 ${c.muted}`}>{entry.message_content}</p>
              )}
              <div className="flex flex-wrap gap-2">
                {entry.post_type && (
                  <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${c.line} ${c.soft}`}>
                    <Radio className="h-3 w-3" />
                    {postTypeLabel(entry.post_type)}{entry.sender ? ` · ${entry.sender}` : ''}
                  </span>
                )}
              </div>
            </div>

            {/* Cell 4: original submission image + communication mockup */}
            <div className={`p-3 border-r ${border}`}>
              <OriginalSubmissionWidget
                r2Key={entry.raw_entry_r2_key}
                theme={theme}
                getToken={getToken}
                bare
                onImageLoaded={(base64, mime) => setSubmissionImage({ base64, mime })}
                postType={entry.post_type}
                messageTypeId={entry.message_type_id}
                messageTypeText={entry.message_type_name}
                communication={
                  entry.post_type === 'email'
                    ? {
                        type: 'email',
                        sender: entry.sender ?? '',
                        subject: entry.headline ?? '',
                        receivedAt: fmtDatePt(entry.created_at),
                        body: entry.message_content ?? '',
                      }
                    : {
                        type: 'sms',
                        sender: entry.sender ?? '',
                        receivedAt: fmtDatePt(entry.created_at),
                        body: entry.message_content ?? '',
                      }
                }
              />
            </div>

            {/* Cell 5: latest obtained screenshots */}
            <div className="p-3">
              <LatestObtainedImagesPanel
                entryId={entry.id}
                userEntryDomainId={entry.user_entry_domain_id}
                latestBrowser={browserRows[0] ?? null}
                theme={theme}
                getToken={getToken}
                bare
                onImagesLoaded={(desktop, mobile) => {
                  setAutoScreenshots({ desktop, mobile })
                }}
              />
            </div>

          </div>
        </article>

        <CampaignForm
          config={{
            mode: 'create',
            processedEntry: entry,
            recentCampaigns,
            submissionImage,
            autoScreenshots,
            browserRows,
            browserRowsLoading,
          }}
          theme={theme}
          getToken={getToken}
          onBack={onBack}
          onDone={onPromoted}
        />
      </div>
    </div>
  )
}
